import {
  OrderDetailAttributes,
} from "@albatrosdeveloper/ave-models-package";
import { CommonModule, DecimalPipe } from "@angular/common";
import { Component, OnInit, OnDestroy } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatChipsModule } from "@angular/material/chips";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatSidenavContainer } from "@angular/material/sidenav";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  PageChangedEvent,
  SortChangedEvent,
  GridReadyEvent,
} from 'app/shared/components/smart-grid';
import { RouteUserCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/route-user-cell-renderer/route-user-cell-renderer.component';
import { DeliverySerialNavigationCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/delivery-serial-navigation-cell-renderer/delivery-serial-navigation-cell-renderer.component';
import { TripNavigationCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/trip-navigation-cell-renderer/trip-navigation-cell-renderer.component';
import { TripCellRendererComponent } from "../../visits/visit-report/cell-renderers/trip-cell-renderer.component";
import { DateHourCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component';
import { CustomerCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/customer-cell-renderer/customer-cell-renderer.component';
import { MaterialCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component';
import { QuantityCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/quantity-cell-renderer/quantity-cell-renderer.component';
import { ActionsCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/actions-cell-renderer/actions-cell-renderer.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { IPaginationResult } from "app/common/interfaces";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import {
  ExportDialogComponent,
  ExportOptions,
} from "app/layout/export-overlay";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { ConfigurationFiltersComponent } from "app/modules/components/filters/configuration-filters/configuration-filters.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { DocumentsFiltersComponent } from "app/modules/components/filters/documents-filters/documents-filters.component";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { MaterialsManagementFiltersComponent } from "app/modules/components/filters/materials-management-filters/materials-management-filters.component";
import { RoutesFiltersComponent } from "app/modules/components/filters/routes-filters/routes-filters.component";
import { StatusFiltersComponent } from "app/modules/components/filters/status-filters/status-filters.component";
import { UserRouteDisplayComponent } from "app/shared/components/user-route-display/user-route-display.component";
import { DocumentFlowButtonComponent } from "app/modules/shared/document-flow-button/document-flow-button.component";
import { DecimalFormatPipe } from "app/pipes/decimal-company.pipe";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { DateHelper } from "app/utils/date.helper";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/status.enum";
import { getCode } from "app/utils/method";
import { Subscription, skip, debounceTime } from "rxjs";
import { DeliveryIncompleteService } from "./delivery-incomplete.service";
import { DELIVERY_INCOMPLETE_TITLE } from "./exports/delivery-incomplete.constants";
import { DeliveryIncompleteExport } from "./exports/delivery-incomplete.export";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { BusinessPartnerCellRendererComponent } from "app/modules/components/monitor/monitor-routes/cell-renderers/business-partner-cell-renderer.component";

@Component({
  selector: "app-delivery-incomplete",
  standalone: true,
  imports: [
    CommonModule,
    HoverMenuDirective,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    SmartGridComponent,
    MatMenuModule,
    FilterComponent,
    MatFormFieldModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    FilterSectionComponent,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
  ],
  providers: [DecimalPipe, DeliveryIncompleteExport],
  templateUrl: "./delivery-incomplete.component.html",
})
export class DeliveryIncompleteHComponent implements OnInit, OnDestroy {
  getCode = getCode;
  private isInitialized = false;
  type = "DT011";

  columnDefs: ColDef<any>[] = [
    {
      colId: "routeUser",
      field: "subRoute.code" as any,
      headerName: "Ruta",
      cellRenderer: RouteUserCellRendererComponent,
      minWidth: 300,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => {
          const subRoute = item.subRoute;
          const user = item.user;
          const routeName = subRoute?.name ?? "-";
          const userName = user?.name ?? "-";
          return `${routeName} - ${userName}`;
        },
        customFilterFn: (rowData: any, filterValue: string) => {
          const subRoute = rowData.subRoute;
          const user = rowData.user;
          const routeName = subRoute?.name ?? "-";
          const userName = user?.name ?? "-";
          const displayValue = `${routeName} - ${userName}`;
          if (!displayValue || displayValue === "- - -") return !filterValue;
          return displayValue === filterValue;
        },
      },
    },

    {
      field: "trip.id",
      headerName: "Viaje",
      minWidth: 150,
      sortable: true,
      cellRenderer: TripCellRendererComponent,
      cellRendererParams: {
        onTripClick: (data: any) => this.goToTravel(data),
      },
    },
    {
      field: "serials",
      headerName: "Nro. Doc.",
      cellRenderer: DeliverySerialNavigationCellRendererComponent,
      cellRendererParams: {
        documentType: this.type, // 'DT011'
      },
      minWidth: 160,
      sortable: true,
    },
    {
      field: "date",
      headerName: "Fecha",
      cellRenderer: DateHourCellRendererComponent,
      minWidth: 140,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => {
          const date = item.date;
          if (!date) return "-";
          return new Date(date).toLocaleDateString("es-ES");
        },
        customFilterFn: (rowData: any, filterValue: string) => {
          const date = rowData.date;
          if (!date) return !filterValue;
          const formattedDate = new Date(date).toLocaleDateString("es-ES");
          return formattedDate === filterValue;
        },
      },
    },
    {
      field: "soldTo.name",
      headerName: "Pagador/Receptor",
      cellRenderer: BusinessPartnerCellRendererComponent,
      autoSize: true,
      minWidth: 250, // Much wider for code + long name
      sortable: true,
      filterOptions: {
        // Display customer name in filter dropdown instead of code
        // Note: Requires shipToName field to be present in row data
        valueExtractor: (item: any) => item?.soldTo?.name,
        customFilterFn(rowData, filterValue) {
          const rowCustomer = rowData?.soldTo?.name;
          if (!rowCustomer) return !filterValue;

          return rowCustomer === filterValue;
        },
      },
    },
    {
      colId: "material",
      field: "material.name" as any,
      headerName: "Material",
      cellRenderer: MaterialCellRendererComponent,
      minWidth: 200,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => {
          return item.material?.name ?? "-";
        },
        customFilterFn: (rowData: any, filterValue: string) => {
          const value = rowData.material?.name;
          if (!value || value === "-") return !filterValue;
          return value === filterValue;
        },
      },
    },
    {
      field: "soldQuantitySuggested",
      headerName: "Cantidad Sugerida",
      cellRenderer: QuantityCellRendererComponent,
      cellRendererParams: {
        quantityField: "soldQuantitySuggested",
        unitField: "soldUnitOfMeasure",
      },
      minWidth: 140,
      sortable: true,
    },
    {
      field: "deliveredQuantity",
      headerName: "Entregado",
      cellRenderer: QuantityCellRendererComponent,
      cellRendererParams: {
        quantityField: "deliveredQuantity",
        unitField: "soldUnitOfMeasure",
      },
      minWidth: 140,
      sortable: true,
    },
    {
      field: "pendingQuantity",
      headerName: "Pendiente",
      cellRenderer: QuantityCellRendererComponent,
      cellRendererParams: {
        quantityField: "pendingQuantity",
        unitField: "soldUnitOfMeasure",
      },
      minWidth: 140,
      sortable: true,
    },
    {
      field: "",
      headerName: "",
      cellRenderer: ActionsCellRendererComponent,
      cellRendererParams: {
        actions: [
          {
            type: "documentFlow",
          },
        ],
      },
      minWidth: 80,
      maxWidth: 80,
      sortable: false,
      pinned: "right",
    },
  ];

  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "_id" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  // Data structures for SmartGrid
  data: any[] = [];
  loading: boolean = false;

  //#region Filter
  filter = {
    dateini: new Date(),
    dateend: new Date(),
  };

  totalItems: number = 0;
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "_id";
  sortDirection: "ASC" | "DESC" = "DESC";
  //#endregion

  //#region properties
  range = new FormGroup({
    start: new FormControl<Date | null>(new Date()),
    end: new FormControl<Date | null>(new Date()),
  });

  //BUSCADOR
  searchSubscription: Subscription;
  //#endregion

  form: FormGroup;

  //Para filtros
  selectedStatuses: number[] = [StatusEnum.ORDER_PARTIAL_DELIVERED]; // Estados por defecto por entrega incompleta

  // Estados ERP
  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  constructor(
    private deliveryIncompleteService: DeliveryIncompleteService,
    private readonly searchService: SearchService,
    private readonly deliveryIncompleteExport: DeliveryIncompleteExport,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private router: Router,
    private fb: FormBuilder
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [],
      end: [],
      startTrip: [],
      endTrip: [],
      startDeliveryDate: [today],
      endDeliveryDate: [today],
      companyId: [],
      salesOfficeId: [],
      documentClassId: [],
      subRouteId: [],
      assignedUserId: [],
      payingBusinessPartnerId: [],
      receivingBusinessPartnerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
    });
  }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    this.onHandleSearch();
    this.getList();
    this.isInitialized = true;
  }

  onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .pipe(skip(1), debounceTime(300))
      .subscribe((term) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = term;
          this.onFilter();
        }
      });
  }

  private getSortField(): keyof OrderDetailAttributes {
    return this.sortActive;
  }

  onFilter() {
    this.getList();
  }

  onClearFilter() {
    this.form.reset({
      startDeliveryDate: new Date(),
      endDeliveryDate: new Date(),
    });
  }

  // SmartGrid event handlers
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.getList();
  }

  onSortChanged(event: SortChangedEvent<any>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as keyof OrderDetailAttributes;
      this.sortDirection = sortState.direction === "asc" ? "ASC" : "DESC";
    } else {
      this.sortActive = "_id";
      this.sortDirection = "DESC";
    }
    this.getList();
  }

  onGridReady(event: GridReadyEvent<any>): void {
    // Can be used for additional initialization if needed
  }

  getList() {
    this.loading = true;
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    const adjustedStart = new Date(this.filter.dateini);
    adjustedStart.setHours(0, 0, 0, 0); // 00:00:00.000

    const adjustedEnd = new Date(this.filter.dateend);
    adjustedEnd.setHours(23, 59, 59, 999); // 23:59:59.999

    let filter: IFilters = {
      type: this.type,
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate: DateHelper.getEndOfDay(this.form.get("end")?.value)
        ? DateHelper.getEndOfDay(this.form.get("end")?.value)
        : DateHelper.getEndOfDay(this.form.get("start")?.value)
          ? DateHelper.getEndOfDay(this.form.get("start")?.value)
          : null,
      startTrip: DateHelper.getStartOfDay(this.form.get("startTrip")?.value),
      endTrip: DateHelper.getEndOfDay(this.form.get("endTrip")?.value)
        ? DateHelper.getEndOfDay(this.form.get("endTrip")?.value)
        : DateHelper.getEndOfDay(this.form.get("startTrip")?.value)
          ? DateHelper.getEndOfDay(this.form.get("startTrip")?.value)
          : null,
      end_startTrip: DateHelper.getStartOfDay(
        this.form.get("end_startTrip")?.value
      ),
      end_endTrip: DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value)
        ? DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value)
        : DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value)
          ? DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value)
          : null,
      startDeliveryDate: DateHelper.getStartOfDay(
        this.form.get("startDeliveryDate")?.value
      ),
      endDeliveryDate: DateHelper.getEndOfDay(
        this.form.get("endDeliveryDate")?.value
      )
        ? DateHelper.getEndOfDay(this.form.get("endDeliveryDate")?.value)
        : DateHelper.getEndOfDay(this.form.get("startDeliveryDate")?.value)
          ? DateHelper.getEndOfDay(this.form.get("startDeliveryDate")?.value)
          : null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      documentClass: this.form.get("documentClassId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //
      soldTo: this.form.get("payingBusinessPartnerId")?.value,
      shipTo: this.form.get("receivingBusinessPartnerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      ordersToFilter: null,
    };

    this.deliveryIncompleteService.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        const data = res?.data?.[0]?.data ?? [];

        this.data = data;
        // Buscar específicamente datos de pagador y receptor
        data.forEach((record, index) => {
          const deliveryDocument =
            record.relatedDocuments?.find((doc) => doc.type === "DELIVERY") ||
            null;

          if (deliveryDocument) {
            this.data[index].deliveryDocument = deliveryDocument;
          }
        });

        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.loading = false;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
        this.loading = false;
      },
    });
  }

  //#region Action
  goToTravel(element: any) {
    if (element.trip?.id) {
      const queryParams = { tripId: element.trip.id };
      const urlTree = this.router.createUrlTree(
        ["monitor", "principal-screen"],
        { queryParams }
      );
      const absoluteUrl =
        window.location.origin + this.router.serializeUrl(urlTree);
      window.open(absoluteUrl, "_blank");
    }
  }

  onExportButtonPressed() {
    if (!this.deliveryIncompleteExport.canStartTask()) return;
    const dialogRef = this.matDialog.open(ExportDialogComponent, {
      data: { title: DELIVERY_INCOMPLETE_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.deliveryIncompleteExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex
        );
      }
    });
  }

  //#endregion
  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
